import { EntityWithId, IActions } from '../interfaces';
import { RestApiId } from '../restApis';
import { Action, Types } from './types';
import { UpdatesState } from './updatesReducer';

export function actions<T, S = RestApiId>(service: S): IActions<T> {
  return {
    requestGet: (
      id: string,
      args: { params?: { [key: string]: string }; query?: { [key: string]: string } },
    ): Action<T, S> => ({
      type: Types.GET_REQUESTED,
      service,
      payload: { id, ...args },
    }),
    startGet: (id: string): Action<T, S> => ({ type: Types.GET_STARTED, service, payload: { id } }),
    completeGet: (entity: EntityWithId<T>): Action<T, S> => ({
      type: Types.GET_COMPLETED,
      service,
      payload: {
        id: entity.id,
        entity: entity.entity,
      },
    }),
    failGet: (id: string, error: string): Action<T, S> => ({
      type: Types.GET_FAILED,
      service,
      payload: {
        id,
        error,
      },
    }),

    requestGetCollection: (args?: {
      clear?: boolean;
      params?: { [key: string]: string };
      query?: { [key: string]: string };
    }): Action<T, S> => ({
      type: Types.GET_COLLECTION_REQUESTED,
      service,
      payload: args || {},
    }),
    startGetCollection: (): Action<T, S> => ({ type: Types.GET_COLLECTION_STARTED, service }),
    completeGetCollection: (entities: EntityWithId<T>[]): Action<T, S> => ({
      type: Types.GET_COLLECTION_COMPLETED,
      service,
      payload: {
        entities,
      },
    }),
    failGetCollection: (error: string): Action<T, S> => ({
      type: Types.GET_COLLECTION_FAILED,
      service,
      payload: {
        error,
      },
    }),

    requestCreate: (tempId: string, entity: T, params?: { [key: string]: string }): Action<T, S> => ({
      type: Types.CREATE_REQUESTED,
      service,
      payload: {
        id: tempId,
        entity,
        params,
      },
    }),
    startCreate: (tempId: string): Action<T, S> => ({
      type: Types.CREATE_STARTED,
      service,
      payload: {
        id: tempId,
      },
    }),
    completeCreate: (tempId: string, entity: EntityWithId<T>): Action<T, S> => ({
      type: Types.CREATE_COMPLETED,
      service,
      payload: {
        id: entity.id,
        tempId,
        entity: entity.entity,
      },
    }),
    failCreate: (tempId: string, error: string): Action<T, S> => ({
      type: Types.CREATE_FAILED,
      service,
      payload: {
        id: tempId,
        error,
      },
    }),

    requestDelete: (id: string, params?: { [key: string]: string }): Action<T, S> => ({
      type: Types.DELETE_REQUESTED,
      service,
      payload: { id, params },
    }),
    startDelete: (id: string): Action<T, S> => ({
      type: Types.DELETE_STARTED,
      service,
      payload: { id },
    }),
    completeDelete: (id: string): Action<T, S> => ({
      type: Types.DELETE_COMPLETED,
      service,
      payload: { id },
    }),
    failDelete: (id: string, error: string): Action<T, S> => ({
      type: Types.DELETE_FAILED,
      service,
      payload: {
        id,
        error,
      },
    }),

    requestUpdate: (id: string, entity: T, params?: { [key: string]: string }): Action<T, S> => ({
      type: Types.UPDATE_REQUESTED,
      service,
      payload: {
        id,
        entity,
        params,
      },
    }),
    startUpdate: (id: string): Action<T, S> => ({
      type: Types.UPDATE_STARTED,
      service,
      payload: { id },
    }),
    completeUpdate: (entity: EntityWithId<T>): Action<T, S> => ({
      type: Types.UPDATE_COMPLETED,
      service,
      payload: {
        id: entity.id,
        entity: entity.entity,
      },
    }),
    failUpdate: (id: string, error: string): Action<T, S> => ({
      type: Types.UPDATE_FAILED,
      service,
      payload: {
        id,
        error,
      },
    }),

    requestPatch: (
      id: string,
      changes: { [key: string]: string },
      params?: { [key: string]: string },
    ): Action<T, S> => ({
      type: Types.PATCH_REQUESTED,
      service,
      payload: {
        id,
        changes,
        params,
      },
    }),
    startPatch: (id: string): Action<T, S> => ({
      type: Types.PATCH_STARTED,
      service,
      payload: {
        id,
      },
    }),
    completePatch: (entity: EntityWithId<T>): Action<T, S> => ({
      type: Types.PATCH_COMPLETED,
      service,
      payload: {
        id: entity.id,
        entity: entity.entity,
      },
    }),
    failPatch: (id: string, error: string): Action<T, S> => ({
      type: Types.PATCH_FAILED,
      service,
      payload: {
        id,
        error,
      },
    }),

    createLocal: (entity: T): Action<T, S> => ({
      type: Types.CREATE_LOCAL,
      service,
      payload: { entity },
    }),
    createLocalWithTemporaryId: (id: string, entity: T): Action<T, S> => ({
      type: Types.CREATE_LOCAL_WITH_TEMPORARY_ID,
      service,
      payload: {
        entity,
        id,
      },
    }),
    updateLocal: (id: string, entity: T): Action<T, S> => ({
      type: Types.UPDATE_LOCAL,
      service,
      payload: {
        id,
        entity,
      },
    }),
    discardLocal: (id: string): Action<T, S> => ({
      type: Types.DISCARD_LOCAL,
      service,
      payload: { id },
    }),
    discardAllLocal: (): Action<T, S> => ({
      type: Types.DISCARD_ALL_LOCAL,
      service,
    }),

    clearAll: (): Action<T, S> => ({
      type: Types.CLEAR_ALL,
      service,
    }),

    restoreUpdates: (updates: UpdatesState<T>): Action<T, S> => ({
      type: Types.RESTORE_UPDATES,
      service,
      payload: updates,
    }),
  };
}
